import type { ReactNode } from 'react';

import { useDebounce, useDeepCompareEffect } from '@pesto/hooks';
import {
  type ColumnFiltersState,
  functionalUpdate,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import * as React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import type { UseDataTableProps } from '../types/SmartTable.types';

import { useConfig } from '../../../providers/ConfigProvider';
import { getColumnFilterKey, getColumnSortKey } from '../utils/helpers';
import { useQueryString } from './useQueryString';

interface DataWithChildren {
  children?: DataWithChildren[] | ReactNode;
}

const searchParamsSchema = z
  .object({
    pageNumber: z.coerce.number().default(1),
    resultsPerPage: z.coerce.number().optional(),
    sort: z.string().optional(),
  })
  .catchall(z.any());

const columnFiltersSchema = z.array(z.object({ id: z.string(), value: z.unknown() }));

export function useDataTable<TData extends DataWithChildren>({
  pageCount = -1,
  filterFields = [],
  enableAdvancedFilter = false,
  method = 'replace',
  scroll = false,
  startTransition,
  syncUrlState = true,
  ...props
}: UseDataTableProps<TData>) {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const [searchParams] = useSearchParams();
  const emptySearchParams = React.useMemo(() => new URLSearchParams(), []);
  const activeSearchParams = syncUrlState ? searchParams : emptySearchParams;
  const { rowsPerPage } = useConfig().pagination;
  const search = searchParamsSchema.parse(Object.fromEntries(activeSearchParams));

  const pageNumber = search.pageNumber;
  const perPage = search.resultsPerPage ?? props.initialState?.pagination?.pageSize ?? rowsPerPage;

  const sort =
    search.sort ?? `${props.initialState?.sorting?.[0]?.id}.${props.initialState?.sorting?.[0]?.desc ? 'desc' : 'asc'}`;

  const [column, order] = sort?.split('.') ?? [];

  const { searchableColumns, filterableColumns } = React.useMemo(() => {
    return {
      searchableColumns: filterFields?.filter((field) => !field.options),
      filterableColumns: filterFields?.filter((field) => field.options),
    };
  }, [filterFields]);

  const { createQueryString } = useQueryString(activeSearchParams);

  const initialColumnFilters: ColumnFiltersState = React.useMemo(() => {
    const createFilter = (column: any, value: string) =>
      ({
        id: String(column.value),
        value: column.options ? value?.split('.') : [value],
      }) satisfies ColumnFiltersState[number];

    return Array.from(activeSearchParams.entries())
      .filter(([key]) => {
        const isFilterableColumn = filterableColumns.some((col) => getColumnFilterKey(col.value) === key);
        const isSearchableColumn = searchableColumns.some((col) => getColumnFilterKey(col.value) === key);

        return isFilterableColumn || isSearchableColumn;
      })
      .map(([key, value]) => {
        const column =
          filterableColumns.find((col) => getColumnFilterKey(col.value) === key) ||
          searchableColumns.find((col) => getColumnFilterKey(col.value) === key);

        return createFilter(column, value);
      });
  }, [activeSearchParams, filterableColumns, searchableColumns]);

  const [rowSelection, setRowSelection] = React.useState({});

  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    props.initialState?.columnVisibility ?? {},
  );

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(initialColumnFilters);

  const urlPagination = React.useMemo(() => ({ pageIndex: pageNumber - 1, pageSize: perPage }), [pageNumber, perPage]);
  const [localPagination, setLocalPagination] = React.useState<PaginationState>(urlPagination);
  const pagination = syncUrlState ? urlPagination : localPagination;
  const { pageIndex, pageSize } = pagination;

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const nextPagination = functionalUpdate(updater, pagination);

    if (!syncUrlState) {
      setLocalPagination(nextPagination);

      return;
    }

    function onUrlChange() {
      const url = `${pathname}?${createQueryString({
        pageNumber: nextPagination.pageIndex + 1,
        resultsPerPage: nextPagination.pageSize,
      })}`;

      navigate(url, { replace: method !== 'push', state: { scroll } });
    }

    if (startTransition) {
      startTransition(onUrlChange);
    } else {
      onUrlChange();
    }
  };

  const getFullColumnId = (sortFilter: string) => {
    const columns = props.columns;

    return columns.find((col) => col?.id?.includes(sortFilter))?.id;
  };

  const [sorting, setSorting] = React.useState<SortingState>([
    {
      id: getFullColumnId(column) ?? '',
      desc: order === 'desc',
    },
  ]);

  const sortFilterName = (sorting: SortingState) => {
    return getColumnSortKey(sorting[0].id);
  };

  React.useEffect(() => {
    if (!syncUrlState) return;

    function onUrlChange() {
      const url = `${pathname}?${createQueryString({
        pageNumber: pageIndex + 1,
        resultsPerPage: pageSize,
        sort: sorting[0]?.id ? `${sortFilterName(sorting)}.${sorting[0]?.desc ? 'desc' : 'asc'}` : null,
      })}`;

      navigate(url, { replace: method !== 'push', state: { scroll } });
    }

    if (startTransition) {
      startTransition(onUrlChange);
    } else {
      onUrlChange();
    }
  }, [
    pageIndex,
    pageSize,
    sorting,
    method,
    scroll,
    startTransition,
    pathname,
    createQueryString,
    navigate,
    syncUrlState,
  ]);

  const debouncedSearchableColumnFilters = columnFiltersSchema.parse(
    JSON.parse(
      useDebounce(
        JSON.stringify(
          columnFilters.filter((filter) => {
            return 'find' in searchableColumns && searchableColumns.find((column) => column.value === filter.id);
          }),
        ),
        500,
      ),
    ),
  );

  const filterableColumnFilters = columnFilters.filter((filter) => {
    return 'find' in filterableColumns && filterableColumns.find((column) => column.value === filter.id);
  });

  const [mounted, setMounted] = React.useState(false);

  const table = useReactTable({
    ...props,
    pageCount,
    state: {
      pagination,
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onPaginationChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    // SAFETY: children are sub-rows of the same table; element children render as full-width rows in DataTable.
    getSubRows: (row) => row.children as TData[] | undefined,
    getExpandedRowModel: getExpandedRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  useDeepCompareEffect(() => {
    if (!syncUrlState) return;

    if (enableAdvancedFilter) return;

    if (!mounted) {
      setMounted(true);

      return;
    }

    const newParamsObject = {
      pageNumber: 1,
    };

    for (const column of debouncedSearchableColumnFilters) {
      const searchText = z.string().safeParse(column.value);

      if (searchText.success) {
        Object.assign(newParamsObject, { [getColumnFilterKey(column.id)]: searchText.data });
      }
    }

    for (const column of filterableColumnFilters) {
      if (Array.isArray(column.value)) {
        Object.assign(newParamsObject, { [getColumnFilterKey(column.id)]: column.value.join('.') });
      }
    }

    for (const key of searchParams.keys()) {
      if (
        ('find' in searchableColumns &&
          searchableColumns.find((column) => getColumnFilterKey(column.value) === key) &&
          !debouncedSearchableColumnFilters.find((column) => getColumnFilterKey(column.id) === key)) ||
        ('find' in filterableColumns &&
          filterableColumns.find((column) => getColumnFilterKey(column.value) === key) &&
          !filterableColumnFilters.find((column) => getColumnFilterKey(column.id) === key))
      ) {
        Object.assign(newParamsObject, { [key]: null });
      }
    }

    function onUrlChange() {
      const url = `${pathname}?${createQueryString(newParamsObject)}`;
      navigate(url, { replace: method !== 'push', state: { scroll } });
    }

    if (startTransition) {
      startTransition(onUrlChange);
    } else {
      onUrlChange();
    }
  }, [debouncedSearchableColumnFilters, filterableColumnFilters, method, scroll, syncUrlState, enableAdvancedFilter]);

  return { table };
}
